import { useCallback, useEffect, useRef, type ReactNode, type SyntheticEvent } from 'react';
import { useToast } from './useToast';

/**
 * Énoncé d'une évaluation que l'apprenant ne doit pas emporter hors de la
 * plateforme (TF-485) : quiz, test de positionnement. Le texte ne se
 * sélectionne pas (appui long des mobiles compris) ; copier, couper, clic
 * droit et glisser y sont bloqués ; tant qu'il est affiché, Ctrl/⌘+S et
 * Ctrl/⌘+P aussi, et l'impression par le menu du navigateur le remplace par
 * une mention. Les champs de saisie gardent sélection, copie et menu :
 * l'apprenant y reprend sa propre réponse.
 *
 * Rien ne s'empêche hors de la page : captures d'écran, « Enregistrer sous »
 * du menu, outils de développement. Le lecteur d'écran lit toujours le texte.
 */

const DELAI_AVIS_MS = 2500;

function dansUnChamp(cible: EventTarget | null): boolean {
  return cible instanceof Element && cible.closest('input, textarea, [contenteditable="true"]') !== null;
}

export function ContenuProtege({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  const notifier = useToast();
  const dernierAvis = useRef(0);

  // Un geste répété (clics droits en rafale) ne doit pas empiler les bulles.
  const prevenir = useCallback(() => {
    const maintenant = Date.now();
    if (maintenant - dernierAvis.current < DELAI_AVIS_MS) return;
    dernierAvis.current = maintenant;
    notifier(`${libelle} reste sur la plateforme : il ne se copie pas, ne s’imprime pas, ne se télécharge pas.`);
  }, [libelle, notifier]);

  useEffect(() => {
    const intercepter = (evenement: KeyboardEvent): void => {
      if (!evenement.ctrlKey && !evenement.metaKey) return;
      const touche = evenement.key.toLowerCase();
      if (touche !== 's' && touche !== 'p') return;
      evenement.preventDefault();
      prevenir();
    };
    document.addEventListener('keydown', intercepter, true);
    return () => document.removeEventListener('keydown', intercepter, true);
  }, [prevenir]);

  const bloquer = (evenement: SyntheticEvent): void => {
    if (dansUnChamp(evenement.target)) return;
    evenement.preventDefault();
    prevenir();
  };

  return (
    <>
      <div
        onCopy={bloquer}
        onCut={bloquer}
        onContextMenu={bloquer}
        onDragStart={bloquer}
        className="select-none [-webkit-touch-callout:none] print:hidden [&_input]:select-text [&_textarea]:select-text"
      >
        {children}
      </div>
      <p className="hidden text-sm text-codialis-navy print:block">{libelle} reste sur la plateforme : il ne s’imprime pas.</p>
    </>
  );
}
